﻿import axios from 'axios';
import JwtUtils from '../utils/jwtUtils';
import Logger from '../utils/logger';
import { formatMessageHistory } from '../utils/chatUtils';
import config from '../config';

// Base API configuration
const API_BASE_URL = config.api.apiUrl;
const DEFAULT_TIMEOUT = config.api.timeout;

// Create axios instance with default config
const apiAgent = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
});

const BACKEND_DOWN_EVENT = 'nixia:notify';
const BACKEND_DOWN_COOLDOWN_MS = 30000;
const MAX_NETWORK_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 500;
let lastBackendDownAt = 0;

// OPTIMIZED: Bounded in-memory cache + in-flight dedupe for GET requests
// Prevents unbounded memory growth under heavy navigation
const MAX_CACHE_SIZE = 200;
const GET_CACHE = new Map();
const INFLIGHT = new Map();

// Evict oldest entries when cache exceeds limit
const evictCache = () => {
  if (GET_CACHE.size <= MAX_CACHE_SIZE) return;
  const toDelete = GET_CACHE.size - Math.floor(MAX_CACHE_SIZE * 0.75);
  const iter = GET_CACHE.keys();
  for (let i = 0; i < toDelete; i++) {
    const { value } = iter.next();
    if (value) GET_CACHE.delete(value);
  }
};

const buildCacheKey = (url, config = {}) => {
  const base = config.baseURL || API_BASE_URL || '';
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${base}${url}|${params}`;
};

export const cachedGet = async (url, config = {}, options = {}) => {
  const {
    ttlMs = 15000,
    dedupe = true,
    bypassCache = false
  } = options;

  const key = buildCacheKey(url, config);
  const now = Date.now();

  if (!bypassCache && GET_CACHE.has(key)) {
    const { expiresAt, data } = GET_CACHE.get(key);
    if (expiresAt > now) {
      return data;
    }
    GET_CACHE.delete(key);
  }

  if (dedupe && INFLIGHT.has(key)) {
    return INFLIGHT.get(key);
  }

  const request = apiAgent.get(url, config)
    .then((response) => {
      if (!bypassCache && ttlMs > 0) {
        GET_CACHE.set(key, { expiresAt: now + ttlMs, data: response.data });
        evictCache();
      }
      return response.data;
    })
    .finally(() => {
      INFLIGHT.delete(key);
    });

  if (dedupe) {
    INFLIGHT.set(key, request);
  }

  return request;
};

// Drop cached GET responses whose URL starts with `urlPrefix`, regardless of
// query params. Use after a write that a same-session GET might otherwise
// keep serving a stale (up to ttlMs-old) snapshot of - e.g. a conversation
// created via /conversations/prepare-nixia not showing up immediately in a
// /conversations/ list fetched moments earlier.
export const invalidateCachedGet = (urlPrefix) => {
  const base = API_BASE_URL || '';
  const matchPrefix = `${base}${urlPrefix}`;
  for (const key of GET_CACHE.keys()) {
    if (key.startsWith(matchPrefix)) {
      GET_CACHE.delete(key);
    }
  }
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const shouldRetryNetworkError = (error) => {
  if (error?.response) {
    return false;
  }
  const method = (error?.config?.method || 'get').toLowerCase();
  const retryableMethods = ['get', 'head', 'options'];
  if (!retryableMethods.includes(method)) {
    return false;
  }
  const code = error?.code || '';
  const message = error?.message || '';
  return (
    code === 'ECONNABORTED' ||
    code === 'ERR_NETWORK' ||
    message.includes('Network Error') ||
    message.includes('ERR_CONNECTION_REFUSED')
  );
};

const notifyBackendDown = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const now = Date.now();
  if (now - lastBackendDownAt < BACKEND_DOWN_COOLDOWN_MS) {
    return;
  }
  lastBackendDownAt = now;
  window.dispatchEvent(
    new CustomEvent(BACKEND_DOWN_EVENT, {
      detail: {
        type: 'error',
        message: 'Le backend est indisponible. Verifiez qu\'il tourne puis reessayez.',
        autoHide: true
      }
    })
  );
};

// Fonction pour obtenir l'organization_id active depuis localStorage
const getActiveOrganizationId = () => {
  try {
    const primaryKey = 'Nixia_active_organization_id';
    const legacyKey = 'nixia_active_organization_id';
    let activeOrgId = localStorage.getItem(primaryKey);

    if (!activeOrgId) {
      const legacyOrgId = localStorage.getItem(legacyKey);
      if (legacyOrgId) {
        localStorage.setItem(primaryKey, legacyOrgId);
        activeOrgId = legacyOrgId;
      }
    }

    return activeOrgId || null;
  } catch (error) {
    return null;
  }
};

// ✅ Fonction pour obtenir l'agent_id actif depuis localStorage
const getActiveAgentId = () => {
  try {
    const primaryKey = 'Nixia_active_agent_id';
    const legacyKey = 'nixia_active_agent_id';
    const agentId = localStorage.getItem(primaryKey) || localStorage.getItem(legacyKey);
    return agentId || null;
  } catch (error) {
    return null;
  }
};

// Request interceptor for adding auth token
// OPTIMIZED: Removed per-request console.logs (thousands per session = GC pressure + CPU waste)
apiAgent.interceptors.request.use(
  config => {
    const token = JwtUtils.getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const activeOrgId = getActiveOrganizationId();
    if (activeOrgId) {
      config.headers['X-Organization-Id'] = activeOrgId;
    }

    const activeAgentId = getActiveAgentId();
    if (activeAgentId) {
      config.headers['X-Agent-Id'] = activeAgentId;
    }

    return config;
  },
  error => {
    Logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
// OPTIMIZED: Removed per-response console.logs, kept only error-path logging
apiAgent.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    if (shouldRetryNetworkError(error)) {
      const cfg = error.config || {};
      cfg.__retryCount = cfg.__retryCount || 0;
      if (cfg.__retryCount < MAX_NETWORK_RETRIES) {
        cfg.__retryCount += 1;
        await sleep(RETRY_BASE_DELAY_MS * cfg.__retryCount);
        return apiAgent(cfg);
      }
      notifyBackendDown();
    }

    Logger.error('Response interceptor error:', {
      status: error.response?.status,
      url: error.config?.url,
      data: error.response?.data
    });

    // ✅ FIX: Amélioration de la gestion des erreurs 403
    // Ne déconnecter que si c'est une vraie erreur d'authentification
    // Routes publiques sans token - ne jamais rediriger vers /login
    const PUBLIC_URL_PATTERNS = [
      '/auth/forgot-password/',
      '/auth/admin-or-user/login',
    ];
    const requestUrl = error.config?.url || '';
    const isPublicRoute = PUBLIC_URL_PATTERNS.some(pattern => requestUrl.includes(pattern));
    const status = error.response?.status;

    if (status === 401 && !isPublicRoute) {
      JwtUtils.removeToken();
      if (window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login?expired=true';
      }
    } else if (status === 403) {
      const errorData = error.response.data;
      const errorCode = errorData?.error_code || errorData?.detail?.error_code;
      
      const authErrorCodes = [
        'AUTHENTICATION_REQUIRED',
        'INVALID_TOKEN',
        'INVALID_TOKEN_FORMAT',
        'USER_NOT_FOUND',
        'USER_INACTIVE'
      ];
      
      if (authErrorCodes.includes(errorCode)) {
        JwtUtils.removeToken();
        if (window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login?expired=true';
        }
      } else {
        // Erreur d'autorisation métier (limite atteinte, etc.) - ne pas déconnecter
        Logger.warn('Authorization error (403) detected:', errorCode);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API methods
export const authAPI = {
  // ✅ FIXED: Login method - Now extracts and stores user_type
  login: async (username, password) => {
    try {
      // ✅ CHANGED: Use unified endpoint instead of /auth/login
      const response = await apiAgent.post('/auth/admin-or-user/login', {
        username,
        password,
      });

      const { token } = response.data;
      if (!token) {
        return {
          success: false,
          error: 'No authentication token received from server',
        };
      }

      const tokenPayload = JwtUtils.decodeToken(token);
      
      const user = {
        username: tokenPayload.sub || username,
        id: tokenPayload.sub,
        // Un compte utilisateur sans type n'est jamais promu admin.
        user_type: tokenPayload.user_type || (tokenPayload.type === 'user' ? 'AGENT_SIMPLE' : 'ADMIN'),
        is_admin: tokenPayload.type === 'admin',
        is_user: tokenPayload.type === 'user'
      };

      JwtUtils.storeToken(token);
      JwtUtils.storeUser(user);

      return { success: true, user };
    } catch (error) {
      Logger.error('Login error:', error);

      let errorMessage = 'Authentication failed. Please check your credentials.';

      if (error.response) {
        const detail = error.response.data?.detail;
        errorMessage = (typeof detail === 'object' ? detail?.message : detail) || errorMessage;
      } else if (error.request) {
        errorMessage = 'Connection timeout or server is unreachable. Please try again later.';
      } else {
        errorMessage = `Request failed: ${error.message}`;
      }

      return { success: false, error: errorMessage };
    }
  },
  requestPasswordResetCode: async (email) => {
    try {
      const response = await apiAgent.post('/auth/forgot-password/request-code', { email });
      return { success: true, data: response.data };
    } catch (error) {
      Logger.error('Request password reset code error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Erreur lors de la demande du code de réinitialisation.'
      };
    }
  },

  verifyPasswordResetCode: async (email, code) => {
    try {
      const response = await apiAgent.post('/auth/forgot-password/verify-code', { email, code });
      return { success: true, data: response.data };
    } catch (error) {
      Logger.error('Verify password reset code error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Code invalide ou expiré.'
      };
    }
  },

  resetPasswordWithCode: async (email, code, newPassword, confirmPassword) => {
    try {
      const response = await apiAgent.post('/auth/forgot-password/reset', {
        email,
        code,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      return { success: true, data: response.data };
    } catch (error) {
      Logger.error('Reset password with code error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Échec de la réinitialisation du mot de passe.'
      };
    }
  },

  // Logout method
  logout: () => {
    JwtUtils.removeToken();
    JwtUtils.removeUser();
    Logger.info('User logged out');
  },

  // Check if user is authenticated (has valid token)
  isAuthenticated: () => {
    const token = JwtUtils.getStoredToken();
    const isValid = !!token && !JwtUtils.isTokenExpired(token);
    return isValid;
  },

  updateProfile: async (profileData) => {
    try {
      const response = await apiAgent.put('/admin/me', profileData);
      return { success: true, data: response.data };
    } catch (error) {
      Logger.error('Update profile error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Erreur lors de la mise à jour du profil',
      };
    }
  },

  getCurrentAdminInfo: async () => {
    try {
      const response = await apiAgent.get('/admin/me');

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      Logger.error('Get current admin info error:', error);

      let errorMessage = 'Failed to fetch user information';

      if (error.response) {
        errorMessage = error.response.data?.detail || errorMessage;
      } else if (error.request) {
        errorMessage = 'Server unreachable. Please try again later.';
      } else {
        errorMessage = `Request failed: ${error.message}`;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // Verify token with backend
  verifyToken: async () => {
    try {
      const token = JwtUtils.getStoredToken();
      if (!token) {
        Logger.info('No token found for verification');
        return { valid: false, error: 'No token found' };
      }

      // First check if token is expired locally to avoid unnecessary API calls
      if (JwtUtils.isTokenExpired(token)) {
        Logger.info('Token expired locally');
        JwtUtils.removeToken();
        JwtUtils.removeUser();
        return { valid: false, error: 'Token expired locally', expired: true };
      }

      const response = await apiAgent.post('/auth/verify-token', { token });
      const result = response.data;

      if (!result.valid) {
        Logger.warning('Token verification failed on server:', result.error);

        // Clean up invalid/expired tokens
        if (result.expired || result.error === 'Token expired') {
          JwtUtils.removeToken();
          JwtUtils.removeUser();
        }

        return result;
      }

      Logger.info('Token verification successful, user_type:', result.user_type);
      
      // ✅ FIX: Return result with user_type included
      return result;
    } catch (error) {
      Logger.error('Token verification error:', error);

      // Handle network errors vs auth errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        JwtUtils.removeToken();
        JwtUtils.removeUser();
        return { valid: false, error: 'Authentication failed', expired: true };
      }

      return { valid: false, error: 'Network error during verification' };
    }
  },

  // Get current user data from storage
  getCurrentUser: () => {
    return JwtUtils.getStoredUser();
  },
};

// Agent management API
export const agentAPI = {
  // ✅ FIX: Get all agents - Le filtrage est maintenant géré automatiquement par le header X-Organization-Id
  getAgents: async () => {
    try {
      // Le filtrage par organization_id est maintenant géré automatiquement
      // via le header X-Organization-Id dans l'interceptor
      const response = await apiAgent.get('/admin/agents');
      return response.data;
    } catch (error) {
      Logger.error('Get agents error:', error);
      throw error;
    }
  },

  // Get a single agent
  getAgent: async agentId => {
    try {
      const response = await apiAgent.get(`/admin/agents/${agentId}`);
      return response.data;
    } catch (error) {
      Logger.error('Get agent error:', error);
      throw error;
    }
  },

  // Create a new agent
  createAgent: async agentData => {
    try {
      const response = await apiAgent.post('/admin/agents', agentData);
      return response.data;
      } catch (error) {
      Logger.error('Create agent error:', error);
      throw error;
    }
  },

  // Update a agent
  updateAgent: async (agentId, agentData) => {
    try {
      const response = await apiAgent.put(
        `/admin/agents/${agentId}`,
        agentData
      );
      return response.data;
    } catch (error) {
      Logger.error('Update agent error:', error);
      throw error;
    }
  },

  // Update agent configuration
  updateAgentConfig: async (agentId, configData) => {
    try {
      const response = await apiAgent.put(
        `/admin/agents/${agentId}/config`,
        configData
      );
      return response.data;
    } catch (error) {
      Logger.error('Update agent config error:', error);
      throw error;
    }
  },

  // Delete a agent
  deleteAgent: async agentId => {
    try {
      const response = await apiAgent.delete(`/admin/agents/${agentId}`);
      return response.data;
    } catch (error) {
      Logger.error('Delete agent error:', error);
      throw error;
    }
  },
};

// Conversation parameters API (motifs / priority classification)
export const conversationParamsAPI = {
  listMotifs: async (apiKey) => {
    const response = await apiAgent.get('/conversation-params/motifs', {
      headers: { 'X-API-Key': apiKey },
    });
    return response.data;
  },

  createMotif: async (apiKey, motifData) => {
    const response = await apiAgent.post('/conversation-params/motifs', motifData, {
      headers: { 'X-API-Key': apiKey },
    });
    return response.data;
  },

  updateMotif: async (apiKey, motifId, motifData) => {
    const response = await apiAgent.put(`/conversation-params/motifs/${motifId}`, motifData, {
      headers: { 'X-API-Key': apiKey },
    });
    return response.data;
  },

  deleteMotif: async (apiKey, motifId) => {
    const response = await apiAgent.delete(`/conversation-params/motifs/${motifId}`, {
      headers: { 'X-API-Key': apiKey },
    });
    return response.data;
  },

  migrateFromConfig: async (apiKey) => {
    const response = await apiAgent.post('/conversation-params/migrate-from-config', {}, {
      headers: { 'X-API-Key': apiKey },
    });
    return response.data;
  },

  classifyMessage: async (apiKey, payload) => {
    const response = await apiAgent.post('/conversation-params/classify', payload, {
      headers: { 'X-API-Key': apiKey },
    });
    return response.data;
  },
};

// Knowledge base API
export const knowledgeAPI = {
  // Get knowledge bases for a specific agent
  getKnowledgeBases: async agentId => {
    try {
      const response = await apiAgent.get(`/knowledge?agent_id=${agentId}`);
      return response.data;
    } catch (error) {
      Logger.error('Get knowledge bases error:', error);
      throw error;
    }
  },

  getKnowledgeBaseContent: async knowledgeId => {
    try {
      const response = await apiAgent.get(`/knowledge/${knowledgeId}/content`);
      return response.data;
    } catch (error) {
      // Backwards-compat / resilience:
      // Some deployments may not expose the /content endpoint even though /knowledge/:id exists.
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        try {
          const fallback = await apiAgent.get(`/knowledge/${knowledgeId}`);
          const kb = fallback.data;
          const raw = kb?.content ?? '';
          const preview =
            typeof raw === 'string'
              ? raw.slice(0, 500) + (raw.length > 500 ? '...' : '')
              : '';
          return {
            ...kb,
            content: typeof raw === 'string' ? raw : '',
            content_preview: preview,
          };
        } catch (fallbackError) {
          Logger.error('Get knowledge base fallback error:', fallbackError);
          throw fallbackError;
        }
      }

      Logger.error('Get knowledge base content error:', error);
      throw error;
    }
  },

  // Add a knowledge base with direct content
  addKnowledgeBase: async (agentId, knowledgeData) => {
    try {
      const data = {
        ...knowledgeData,
        agent_id: agentId,
      };
      const response = await apiAgent.post('/knowledge', data);
      return response.data;
    } catch (error) {
      Logger.error('Add knowledge base error:', error);
      throw error;
    }
  },

  // Upload a file as a knowledge base
  uploadKnowledgeBase: async (agentId, formData, options = {}) => {
    try {
      // Make sure agent_id is included in formData
      if (!formData.has('agent_id')) {
        formData.append('agent_id', agentId);
      }

      const response = await apiAgent.post('/knowledge/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Un PDF illustre est lu page par page par l'IA : bien plus long que les 60 s par defaut.
        timeout: 300000,
        onUploadProgress: options.onUploadProgress,
      });
      return response.data;
    } catch (error) {
      Logger.error('Upload knowledge base error:', error);
      throw error;
    }
  },

  // Get a specific knowledge base
  getKnowledgeBase: async knowledgeId => {
    try {
      const response = await apiAgent.get(`/knowledge/${knowledgeId}`);
      return response.data;
    } catch (error) {
      Logger.error('Get knowledge base error:', error);
      throw error;
    }
  },

  // Update a knowledge base (name, description, content)
  updateKnowledgeBase: async (knowledgeId, updateData) => {
    try {
      const response = await apiAgent.put(`/knowledge/${knowledgeId}`, updateData);
      return response.data;
    } catch (error) {
      Logger.error('Update knowledge base error:', error);
      throw error;
    }
  },

  // Delete a knowledge base
  deleteKnowledgeBase: async knowledgeId => {
    try {
      const response = await apiAgent.delete(`/knowledge/${knowledgeId}`);
      return response.data;
    } catch (error) {
      Logger.error('Delete knowledge base error:', error);
      throw error;
    }
  },
};

// Scraping API - NOUVELLE SECTION AJOUTÉE
export const scrapingAPI = {
  // Manual scraping with custom URL
  manualScraping: async (agentId, url) => {
    try {
      const response = await apiAgent.post(`/scraping/manual/${agentId}`, {
        url: url
      });
      return response.data;
    } catch (error) {
      Logger.error('Manual scraping error:', error);
      throw error;
    }
  },

  // Trigger scraping with agent's existing website
  triggerScraping: async (agentId) => {
    try {
      const response = await apiAgent.post(`/scraping/trigger/${agentId}`);
      return response.data;
    } catch (error) {
      Logger.error('Trigger scraping error:', error);
      throw error;
    }
  },

  // Get scraping status
  getScrapingStatus: async (agentId) => {
    try {
      const response = await apiAgent.get(`/scraping/status/${agentId}`);
      return response.data;
    } catch (error) {
      Logger.error('Get scraping status error:', error);
      throw error;
    }
  },

  // Get scraping job status/progress
  getScrapingJob: async (jobId) => {
    try {
      const response = await apiAgent.get(`/scraping/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      Logger.error('Get scraping job error:', error);
      throw error;
    }
  },

  // Enrich knowledge base
  enrichKnowledgeBase: async (agentId) => {
    try {
      const response = await apiAgent.post(`/scraping/enrich/${agentId}`);
      return response.data;
    } catch (error) {
      Logger.error('Enrich knowledge base error:', error);
      throw error;
    }
  }
};

// Chat API methods
export const chatAPI = {
  // Get chats for a agent
  getAgentChats: async agentId => {
    try {
      const response = await apiAgent.get(`/chats?agent_id=${agentId}`);
      return response.data;
    } catch (error) {
      Logger.error('Get chats error:', error);
      throw error;
    }
  },

  // Send a message to the chatbot
  sendMessage: async (
    apiKey,
    message,
    sessionId,
    aiEngine = null,
    aiModel = null,
    messageHistory = [],
    clientId = null,  // NEW: Add client_id parameter
    messageType = 'TEXT', // NEW: Add message_type parameter
    mediaData = null, // NEW: Add media_data parameter
    additionalText = null // NEW: Add additional_text parameter
  ) => {
    try {
      // Build request payload with optional AI engine and model selection
      const payload = {
        content: message,
        message_history: formatMessageHistory(messageHistory),
        message_type: messageType, // NEW
      };

      // Add media data if present
      if (mediaData) {
        payload.media_data = mediaData;
      }
      
      if (additionalText) {
        payload.additional_text = additionalText;
      }

      // Add AI engine and model if provided
      if (aiEngine) {
        payload.ai_engine = aiEngine;
      }
      if (aiModel) {
        payload.ai_model = aiModel;
      }
      if (clientId) {
        payload.client_id = clientId;
      }

      const response = await axios.post(
        `${API_BASE_URL}/chat/message`,
        payload,
        {
          headers: {
            'X-API-Key': apiKey,
            'Session-Id': sessionId,
            'Content-Type': 'application/json',
          },
          timeout: 300000, // Increased timeout for AI responses 
        }
      );

      // ✅ FIX: Vérifier si le contenu contient un message d'erreur
      const content = response.data.content || '';
      const isErrorMessage = content.startsWith('⚠️') || content.startsWith('Erreur:');

      // Return a normalized response format to handle different response structures
      return {
        id: response.data.id || `msg-${Date.now()}`,
        content: content,
        message: content, // For backward compatibility
        role: response.data.role || 'assistant',
        timestamp: response.data.created_at || new Date().toISOString(),
        engine: aiEngine,
        model: aiModel,
        isError: isErrorMessage, // ✅ Nouveau champ pour indiquer une erreur
        message_type: response.data.message_type || 'TEXT', // NEW
        media_url: response.data.media_url, // NEW
        transcription: response.data.transcription, // NEW
      };
    } catch (error) {
      Logger.error('Send message error:', error);
      // Add detailed error logging
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        Logger.error('Server error response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });
      } else if (error.request) {
        // The request was made but no response was received
        Logger.error('No response received:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        Logger.error('Request error:', error.message);
      }
      throw error;
    }
  },
};
// Admin stats API
export const adminStatsAPI = {
  // Get admin statistics
  getAdminStats: async () => {
    try {
      const response = await apiAgent.get('/admin/stats');
      return response.data;
    } catch (error) {
      Logger.error('Get admin stats error:', error);
      throw error;
    }
  },
};

export { apiAgent };
